<template>
  <div
    v-if="hasData"
    class="insights-meetings-widget-spent"
  >
    <AppChartLegend
      v-if="withLegend"
      :values="chartData"
      :include-values="false"
    />

    <AppChartDonut
      :values="chartData"
      :text="percentage"
      :sort="false"
      :size="chartSize"
      :inner-radius="chartRadius"
      with-tooltips
    >
      <template #details>
        <div class="widget-details">
          <div class="property">
            <div>{{ number }}</div>
            Meetings
          </div>
          <div
            v-if="cost"
            class="property"
          >
            <div>{{ cost }}</div>
            Spent
            <i
              v-if="showWarningIcon"
              v-tooltip="tooltipOptions"
              class="hi hi-warning ml-5"
              :title="payRateTooltip"
            />
          </div>
        </div>
      </template>
    </AppChartDonut>
  </div>
  <div v-else>No data available.</div>
</template>

<script>
import { mapGetters } from 'vuex';
import AppChartDonut from '~/components/AppChartDonut.vue';
import AppChartLegend from '~/components/AppChartLegend.vue';
import { formatDuration } from '~/utils/time_utils';
import { InsightsTracking } from '~/mixins';

export default {
  name: 'InsightsMeetingsTimeSpent',
  components: {
    AppChartDonut,
    AppChartLegend,
  },
  mixins: [InsightsTracking],
  props: {
    chartSize: {
      type: Number,
      default: 90,
    },
    chartRadius: {
      type: Number,
      default: 38,
    },
    withLegend: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    tooltipOptions: Object.freeze({
      placement: 'top',
    }),
  }),
  computed: {
    ...mapGetters(['meetings']),
    hasData() {
      return this.meetings?.total && 'number' in this.meetings.total && 'cost' in this.meetings.total;
    },
    rate() {
      return this.meetings.total.pay_rate;
    },
    showWarningIcon() {
      return this.rate && 'count' in this.rate && 'total' in this.rate && this.rate.count !== this.rate.total;
    },
    payRateTooltip() {
      if (this.rate.count === 0 && this.rate.total === 1) {
        return 'The team member does not have a pay rate set';
      }

      const str =
        this.rate.count > 0 ? `Only ${this.rate.count} of ${this.rate.total}` : `None of the ${this.rate.total}`;

      return `${str} members have a pay rate set`;
    },
    percentage() {
      return this.meetings.total.percentage ? `${Math.round(this.meetings.total.percentage)}%` : '0';
    },
    number() {
      return this.meetings.total?.number || 0;
    },
    cost() {
      const cost = this.meetings.total?.cost;

      if (cost === null) {
        return false;
      }

      return Number(cost).formatMoney({ decimals: 0 });
    },
    chartData() {
      // Consider 100% as the total worked time and subtract the meetings percentage from it
      const meetingsPercentage = this.meetings.total.percentage || 0;
      const total = 100 - meetingsPercentage;

      return [
        {
          key: 'meetings',
          label: 'Meetings',
          value: meetingsPercentage,
          tooltip: this.formatTooltip(this.meetings.total.time?.meetings),
        },
        {
          key: 'total',
          label: 'Total work time',
          value: total,
          tooltip: this.formatTooltip(this.meetings.total.time?.total),
        },
      ];
    },
  },
  methods: {
    formatTooltip(value) {
      return value ? `${formatDuration(value, { showSeconds: 'never' }).replace(/:/g, 'h')}m` : '';
    },
  },
};
</script>
